/**
 * Settings › Account (part 36a's rows, part 36c's page; `cockatrice-ui`: `Account.jsx`): the account's address, password
 * and purchase; the second sign-in step, off with its warning (decision 24) or each factor with Remove; the devices with
 * Remove; the changes waiting with Cancel (PD); the account's activity (NO1); the data: export, and delete account with
 * its confirm form (DA1). Without an account, the way to sign up or log in; with accounts off, what comes later. Every
 * action opens its sheet (`account-sheet.ts`) or runs its proof in the form (C9).
 */
import { createMemo, createSignal, onCleanup, untrack } from 'solid-js';
import type { Accessor } from 'solid-js';
import { LEGAL_VERSIONS } from '../account/legal.ts';
import type { Listed } from '../account/manager.ts';
import type { ChangeEntry, DeviceEntry, Factors, Granted, Notice, SecondFactorNeeded } from '../account/wire.ts';
import type { SecondProof } from '../account/flows.ts';
import { askPasskey } from '../account/passkey.boundary.ts';
import { ErrorText, focusField, Submit } from './account-fields.ts';
import { actionProblem, changeWords, dateWords, PasteLink, SheetPassword, timeWords } from './account-sheet.ts';
import type { AccountTask } from './account-sheet.ts';
import Badge from '../ui/Badge.ts';
import Button from '../ui/Button.ts';
import Card from '../ui/Card.ts';
import Icon from '../ui/Icon.ts';
import List, { ListItem } from '../ui/List.ts';
import SettingsRow from '../ui/SettingsRow.ts';
import TextField from '../ui/TextField.ts';
import { component, el, each, when, whenSome } from '../ui/solid.ts';
import type { Child } from '../ui/solid.ts';
import { Later, Row, Section } from './setting-rows.ts';
import type { Shell } from './shell.ts';

/** Opens `task`'s sheet. */
function open(shell: Shell, task: AccountTask): void {
  shell.ui.setDialog({ kind: 'account', task });
}

/** A line of help under a section's heading, or a list's state (the page's help words). */
function Help(className: string, ...words: readonly Child[]): Child {
  return el('p', { class: `ac-hint ${className}`.trim() }, ...words);
}

/** A list's state in the page: its rows once read, "Loading…", or its failure with Try again. */
function ListOf<T>(shell: Shell, list: () => Listed<T>, rows: (value: Accessor<T>) => Child): Child {
  const { t, manager } = shell;
  return () => {
    const now = list();
    if (now.kind === 'loading') return Help('', t('account.page.loading'));
    if (now.kind === 'failed') return Help('ac-list-failed', `${t('account.page.failed')} `, component(Button, { variant: 'link', onClick: () => void manager.load(true), children: t('account.page.retry') }));
    return rows(() => now.value);
  };
}

/** SP17: the trial or the purchase in words. */
function standingWords(shell: Shell): string {
  const { t, account } = shell;
  const now = account.standing();
  if (now.kind === 'paid') return t('settings.accountPaid');
  if (now.kind === 'trial') return t('settings.accountTrial', { count: now.daysLeft });
  if (now.kind === 'ended') return t('settings.accountEnded');
  // 'unknown' (no copy confirmed on this device); 'none' never shows here, the rows being an account's.
  return t('settings.accountUnknown');
}

/** C8: logging out, its confirmation saying what it does. */
function LogOut(shell: Shell): Child {
  const { t, account } = shell;
  const [confirming, setConfirming] = createSignal(false);
  return Row(
    'setting-log-out',
    t('settings.logOut'),
    t('settings.logOutHelp'),
    when(
      confirming,
      () =>
        el(
          'div',
          { class: 'ac-actions', role: 'group', 'aria-label': t('settings.logOutConfirm') },
          component(Button, {
            variant: 'primary',
            id: 'setting-log-out',
            class: 'log-out-confirm',
            'aria-describedby': 'setting-log-out-help',
            ref: (button: HTMLButtonElement) => {
              queueMicrotask(() => {
                button.focus();
              });
            },
            onClick: () => {
              setConfirming(false);
              void account.logOut().then(() => {
                document.getElementById('setting-account-start')?.focus();
              });
            },
            children: t('settings.logOutConfirm'),
          }),
          component(Button, {
            onClick: () => {
              setConfirming(false);
              queueMicrotask(() => document.getElementById('setting-log-out')?.focus());
            },
            children: t('chapter.cancel'),
          }),
        ),
      () => component(Button, { id: 'setting-log-out', class: 'log-out', 'aria-describedby': 'setting-log-out-help', onClick: () => setConfirming(true), children: t('settings.logOut') }),
    ),
    'group',
  );
}

/** The Account section's rows of an account: email, password, the purchase, the terms, signed out, log out. */
function AccountRows(shell: Shell, email: string, signedOut: boolean): Child {
  const { t, ui, manager, account } = shell;
  const live = !signedOut;
  const site = account.site();
  return [
    Row(
      'setting-account-email',
      t('account.email'),
      email,
      live ? component(Button, { size: 'sm', id: 'setting-account-email', class: 'change-email', 'aria-describedby': 'setting-account-email-help', 'aria-haspopup': 'dialog', onClick: () => { open(shell, { kind: 'email' }); }, children: t('account.page.changeEmail') }) : null,
      'group',
    ),
    live
      ? Row(
          'setting-account-password',
          t('account.password'),
          t('account.page.passwordHelp'),
          component(Button, { size: 'sm', id: 'setting-account-password', class: 'change-password', 'aria-describedby': 'setting-account-password-help', 'aria-haspopup': 'dialog', onClick: () => { open(shell, { kind: 'password' }); }, children: t('account.page.changePassword') }),
          'group',
        )
      : null,
    // The design's "Cockatrice" row: the trial or the purchase, no control yet (the checkout is fix-next 56's).
    component(SettingsRow, { label: t('app.name'), help: el('span', { class: 'account-standing' }, () => standingWords(shell)) }),
    // TA1: the terms asked again, never blocking (C16).
    whenSome(
      () => {
        const terms = manager.terms();
        return terms?.required === true ? terms : null;
      },
      () =>
        Row(
          'setting-account-terms',
          t('account.page.terms'),
          t('account.banner.terms.text'),
          el(
            'div',
            { class: 'ac-actions' },
            component(Button, { variant: 'link', href: `${site}/terms/`, target: '_blank', rel: 'noopener noreferrer', children: t('account.create.terms') }),
            component(Button, { variant: 'link', href: `${site}/privacy/`, target: '_blank', rel: 'noopener noreferrer', children: t('account.create.privacy') }),
            AcceptTerms(shell, 'setting-account-terms'),
          ),
          'group',
        ),
    ),
    signedOut
      ? Row(
          'setting-account-again',
          t('settings.accountSignedOut'),
          t('settings.accountSignedOutHelp'),
          component(Button, { variant: 'primary', id: 'setting-account-again', 'aria-describedby': 'setting-account-again-help', onClick: () => ui.setAccountStep('login'), children: t('account.login.submit') }),
          'group',
        )
      : null,
    LogOut(shell),
  ];
}

/** TA2: Accept, with the app's versions; a stale app is told to reload. */
export function AcceptTerms(shell: Shell, id: string): Child {
  const { t, manager } = shell;
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const accept = async (): Promise<void> => {
    if (busy()) return;
    setBusy(true);
    const outcome = await manager.acceptTerms(LEGAL_VERSIONS);
    setBusy(false);
    setError(outcome.kind === 'ok' ? '' : actionProblem(shell, outcome));
  };
  return [
    component(Button, {
      variant: 'primary',
      size: 'sm',
      class: 'accept-terms',
      'aria-describedby': `${id}-error`,
      get 'aria-busy'() {
        return busy() ? 'true' : 'false';
      },
      onClick: () => void accept(),
      children: t('account.banner.terms.accept'),
    }),
    ErrorText(`${id}-error`, error),
  ];
}

/** FX1, FX5: the second sign-in step: off with its warning, or each factor and what can be done with it. */
function FactorSection(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, manager } = shell;
  const on = (factors: Factors): boolean => factors.totp !== null || factors.passkeys.length > 0;
  // The task is made at the press (whether this factor is the last one is read then).
  const remove = (label: string, task: () => AccountTask): Child =>
    component(Button, { variant: 'ghost', size: 'sm', class: 'remove-factor', 'aria-label': label, 'aria-haspopup': 'dialog', onClick: () => { open(shell, task()); }, children: t('account.factor.remove') });
  const off = (): Child =>
    component(SettingsRow, {
      ids: 'setting-factor',
      label: el('span', { class: 'ac-inline' }, t('account.factor.off'), component(Badge, { children: t('account.factor.recommended') })),
      help: t('account.factor.offHelp'),
      children: component(Button, {
        variant: 'primary',
        size: 'sm',
        id: 'setting-factor',
        class: 'set-up-factor',
        'aria-describedby': 'setting-factor-label setting-factor-help',
        'aria-haspopup': 'dialog',
        onClick: () => {
          open(shell, { kind: 'factor' });
        },
        children: t('account.factor.setUp'),
      }),
    });
  const onRows = (factors: Accessor<Factors>): Child => {
    const count = (): number => (factors().totp === null ? 0 : 1) + factors().passkeys.length;
    return [
      component(SettingsRow, { label: t('account.factor.on'), help: t('account.factor.onHelp') }),
      whenSome(
        () => factors().totp,
        (totp) =>
          Row(
            'setting-factor-app',
            t('account.factor.app'),
            t('account.factor.added', { date: dateWords(shell, totp().createdAt) }),
            remove(t('account.factor.removeApp'), () => ({ kind: 'remove-factor', factor: { kind: 'totp' }, last: count() === 1 })),
            'group',
          ),
      ),
      each(
        () => factors().passkeys,
        (passkey) =>
          Row(
            `setting-passkey-${passkey.id}`,
            t('account.factor.passkey'),
            t('account.factor.added', { date: dateWords(shell, passkey.createdAt) }),
            remove(t('account.factor.removePasskey', { date: dateWords(shell, passkey.createdAt) }), () => ({ kind: 'remove-factor', factor: { kind: 'passkey', id: passkey.id }, last: count() === 1 })),
            'group',
          ),
      ),
      Row(
        'setting-factor-codes',
        t('account.factor.codes', { count: factors().backupCodes }),
        t('account.factor.codesHelp'),
        component(Button, { size: 'sm', id: 'setting-factor-codes', class: 'new-codes', 'aria-describedby': 'setting-factor-codes-help', 'aria-haspopup': 'dialog', onClick: () => { open(shell, { kind: 'codes' }); }, children: t('account.factor.newCodes') }),
        'group',
      ),
      el('div', { class: 'ac-actions ac-add-factor' }, component(Button, { size: 'sm', class: 'add-factor', 'aria-haspopup': 'dialog', onClick: () => { open(shell, { kind: 'factor' }); }, children: factors().totp === null ? t('account.factor.addApp') : t('account.factor.addPasskey') })),
    ];
  };
  return Section('settings-factor', t('account.page.factor'), heading, ListOf(shell, () => manager.factors(), (factors) => () => (on(factors()) ? onRows(factors) : off())));
}

/** DV1, DV4: a device's name: when it was added (the relay keeps no name: decision 57); this one has its chip. */
function deviceName(shell: Shell, device: DeviceEntry): string {
  return shell.t('account.device.name', { date: dateWords(shell, device.ageFrom) });
}

/** A row's mark in a list (the library's Icon). */
function Mark(name: 'devices' | 'history'): Child {
  return component(Icon, { name });
}

/** DV1–DV3: the devices, this one first, each with its chips and, while live and not this one, Remove. */
function DevicesSection(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, manager } = shell;
  const chip = (text: string): Child => component(Badge, { children: text });
  return Section(
    'settings-devices',
    t('account.page.devices'),
    heading,
    Help('ac-section-help', t('account.page.devicesHelp')),
    ListOf(shell, () => manager.devices(), (devices) =>
      component(List, {
        class: 'ac-devices',
        get children() {
          return each(devices, (device) =>
            component(ListItem, {
              class: 'ac-device',
              leading: Mark('devices'),
              title: el(
                'span',
                { class: 'ac-inline' },
                deviceName(shell, device),
                device.current ? chip(t('account.device.this')) : null,
                device.new ? chip(t('account.device.new')) : null,
                device.revokedAt === null ? null : chip(t('account.device.removed')),
                device.loggedOutAt === null || device.revokedAt !== null ? null : chip(t('account.device.loggedOut')),
              ),
              description: t('account.device.used', { date: dateWords(shell, device.lastUsedAt) }),
              // DV3: a removed or logged-out device shows no Remove.
              trailing:
                device.current || device.revokedAt !== null || device.loggedOutAt !== null
                  ? null
                  : component(Button, {
                      variant: 'ghost',
                      size: 'sm',
                      class: 'remove-device',
                      'aria-label': t('account.device.removeName', { date: dateWords(shell, device.ageFrom) }),
                      'aria-haspopup': 'dialog',
                      onClick: () => {
                        open(shell, { kind: 'revoke', device });
                      },
                      children: t('account.device.remove'),
                    }),
            }),
          );
        },
      }),
    ),
  );
}

/** This device's ID, from the device list once read (null before, or when it couldn't be read). */
function selfId(shell: Shell): string | null {
  const devices = shell.manager.devices();
  const list = devices.kind === 'ok' ? devices.value : [];
  return list.find((device) => device.current)?.id ?? null;
}

/** PD1–PD5: the changes waiting, each with what it is, from which device, until when, and Cancel. */
function WaitingSection(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, manager } = shell;
  const when_ = (change: ChangeEntry): string =>
    change.state === 'pending' ? t('account.change.pending', { date: timeWords(shell, change.dueAt) }) : t('account.change.open', { date: timeWords(shell, change.expiresAt) });
  return Section(
    'settings-waiting',
    t('account.page.waiting'),
    heading,
    Help('ac-section-help', t('account.page.waitingHelp')),
    ListOf(shell, () => manager.changes(), (changes) =>
      when(
        () => changes().length > 0,
        () =>
          component(List, {
            class: 'ac-devices ac-changes',
            get children() {
              return each(changes, (change) =>
                component(ListItem, {
                  class: 'ac-device',
                  leading: Mark('history'),
                  title: changeWords(shell, change),
                  description: `${when_(change)} ${change.device === selfId(shell) ? t('account.change.here') : t('account.change.there')}`,
                  trailing: component(Button, {
                    variant: 'ghost',
                    size: 'sm',
                    class: 'cancel-change',
                    'aria-label': t('account.change.cancelName', { change: changeWords(shell, change) }),
                    'aria-haspopup': 'dialog',
                    onClick: () => {
                      open(shell, { kind: 'cancel', change });
                    },
                    children: t('account.change.cancel'),
                  }),
                }),
              );
            },
          }),
        () => Help('', t('account.page.waitingNone')),
      ),
    ),
    // Deep-fix r4, LK: a link pasted in here outlives the sheet that asked for it (the iOS Home Screen app's open in the
    // browser, and the app may be reloaded meanwhile).
    PasteRow(shell),
  );
}

/** The paste row: a change's or an enrolment's link, opened here, on the device that asked. */
function PasteRow(shell: Shell): Child {
  const { t } = shell;
  const [error, setError] = createSignal('');
  return component(SettingsRow, {
    class: 'paste-row',
    label: t('account.paste.title'),
    help: t('account.paste.rowHelp'),
    children: el('div', { class: 'ac-paste' }, PasteLink(shell, setError, 'paste-row-link', 'paste-row-error'), ErrorText('paste-row-error', error)),
  });
}

/** NO1: the account's newest notices, each in words with its date and where. */
function ActivitySection(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, manager } = shell;
  const where = (notice: Notice): string => {
    if (notice.device === null) return t('account.notice.nowhere');
    return notice.device === selfId(shell) ? t('account.notice.here') : t('account.notice.there');
  };
  return Section(
    'settings-activity',
    t('account.page.activity'),
    heading,
    ListOf(shell, () => manager.notices(), (notices) =>
      when(
        () => notices().length > 0,
        () =>
          component(List, {
            class: 'ac-activity',
            dense: true,
            get children() {
              return each(notices, (notice) => component(ListItem, { title: t(`account.notice.${notice.kind}`), description: t('account.notice.when', { date: timeWords(shell, notice.at), where: where(notice) }) }));
            },
          }),
        () => Help('', t('account.page.activityNone')),
      ),
    ),
  );
}

/** XP1: the export: the relay's answer as a file the writer saves. */
function ExportRow(shell: Shell): Child {
  const { t, manager } = shell;
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [file, setFile] = createSignal<{ readonly url: string; readonly name: string } | null>(null);
  onCleanup(() => {
    const made = file();
    if (made !== null) shell.revokeUrl(made.url);
  });
  const run = async (): Promise<void> => {
    if (busy()) return;
    setBusy(true);
    setError('');
    const outcome = await manager.exportData();
    setBusy(false);
    if (outcome.kind !== 'ok') {
      setError(actionProblem(shell, outcome));
      return;
    }
    const before = file();
    if (before !== null) shell.revokeUrl(before.url);
    const name = `cockatrice-account-${new Date(shell.now()).toISOString().slice(0, 10)}.json`;
    setFile({ url: shell.fileUrl(new TextEncoder().encode(`${JSON.stringify(outcome.value, null, 2)}\n`), 'application/json'), name });
    queueMicrotask(() => document.querySelector<HTMLElement>('.save-export')?.focus());
  };
  return Row(
    'setting-export',
    t('account.data.export'),
    t('account.data.exportHelp'),
    el(
      'div',
      { class: 'ac-actions' },
      component(Button, {
        size: 'sm',
        id: 'setting-export',
        class: 'export-data',
        'aria-describedby': 'setting-export-help setting-export-error',
        get 'aria-busy'() {
          return busy() ? 'true' : 'false';
        },
        onClick: () => void run(),
        get children() {
          return busy() ? t('account.working') : t('account.data.export');
        },
      }),
      whenSome(file, (made) =>
        component(Button, {
          variant: 'primary',
          size: 'sm',
          class: 'save-export',
          get href() {
            return made().url;
          },
          get download() {
            return made().name;
          },
          get children() {
            return t('account.data.save', { name: made().name });
          },
        }),
      ),
      ErrorText('setting-export-error', error),
    ),
    'group',
  );
}

/** The delete form's step: the password, or the second step. */
type DeleteStep = { readonly kind: 'password' } | { readonly kind: 'factor'; readonly need: SecondFactorNeeded };

/** DA1, 3.3.6: Delete account…: the confirm form (what goes, BK17's books with it), the password, then F if on. */
function DeleteRow(shell: Shell): Child {
  const { t, manager } = shell;
  const [opened, setOpened] = createSignal(false);
  const [step, setStep] = createSignal<DeleteStep>({ kind: 'password' });
  const [password, setPassword] = createSignal('');
  const [code, setCode] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const [asked, setAsked] = createSignal<number | null>(null);
  const close = (): void => {
    setOpened(false);
    setStep({ kind: 'password' });
    setError('');
    queueMicrotask(() => document.getElementById('setting-delete')?.focus());
  };
  const ask = async (grant: Granted): Promise<void> => {
    const outcome = await manager.askDeletion(grant.grant);
    if (outcome.kind !== 'ok') {
      setError(actionProblem(shell, outcome));
      return;
    }
    setAsked(outcome.value.dueAt);
    setOpened(false);
    queueMicrotask(() => document.getElementById('setting-delete-asked')?.focus());
  };
  const proved = async (proof: SecondProof, need: SecondFactorNeeded): Promise<void> => {
    setBusy(true);
    const grant = await manager.proveSecond(need.ticket, proof);
    if (grant.kind === 'ok') await ask(grant.value);
    else setError(actionProblem(shell, grant));
    setBusy(false);
  };
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (busy()) return;
    setError('');
    const now = step();
    if (now.kind === 'factor') {
      if (code().trim() === '') {
        setError(t('account.error.noCode'));
        focusField('delete-code');
        return;
      }
      const typed = code().trim();
      await proved(/^\d[\d\s]*$/u.test(typed) ? { totp: typed.replace(/\s/gu, '') } : { backupCode: typed }, now.need);
      return;
    }
    if (password() === '') {
      setError(t('account.error.password'));
      focusField('delete-pw');
      return;
    }
    setBusy(true);
    const first = await manager.prove(password());
    if (first.kind !== 'ok') setError(actionProblem(shell, first));
    else if (first.value.kind === 'second-factor') {
      setStep({ kind: 'factor', need: first.value });
      queueMicrotask(() => document.getElementById('delete-code')?.focus());
    } else await ask(first.value);
    setBusy(false);
  };
  const passkey = (need: SecondFactorNeeded): Child => {
    const request = need.passkey;
    return request === null
      ? null
      : component(Button, {
          class: 'delete-passkey',
          onClick: () => {
            void askPasskey(shell.credentials, request).then((answer) => {
              if (answer === null) setError(t('account.error.passkey'));
              else void proved({ passkey: answer }, need);
            });
          },
          children: t('account.second.passkey'),
        });
  };
  return [
    component(SettingsRow, {
      ids: 'setting-delete',
      label: t('account.data.delete'),
      help: t('account.data.deleteHelp'),
      children: component(Button, {
        size: 'sm',
        id: 'setting-delete',
        class: 'delete-account',
        get 'aria-expanded'() {
          return String(opened());
        },
        'aria-controls': 'ac-delete',
        'aria-describedby': 'setting-delete-help',
        onClick: () => setOpened(true),
        children: t('account.data.deleteOpen'),
      }),
    }),
    whenSome(asked, (dueAt) => el('p', { id: 'setting-delete-asked', class: 'ac-note', tabIndex: -1, role: 'status' }, component(Icon, { name: 'info', size: 'sm' }), el('span', {}, () => t('account.delete.asked', { date: dateWords(shell, dueAt()) })))),
    when(opened, () =>
      // The design's confirm box (the library's Card, its danger mark before the question), the form inside it.
      component(Card, {
        tone: 'danger',
        class: 'ac-delete-box',
        title: el('span', { id: 'ac-delete-title' }, t('account.delete.title')),
        children: el(
          'form',
          {
            id: 'ac-delete',
            class: 'ac-confirm__form',
            'aria-labelledby': 'ac-delete-title',
            noValidate: true,
            onSubmit: (event: SubmitEvent) => void submit(event),
            onKeyDown: (event: KeyboardEvent) => {
              if (event.key !== 'Escape') return;
              event.preventDefault();
              close();
            },
          },
          el('ul', { class: 'ac-confirm__list' }, el('li', {}, t('account.delete.server')), el('li', {}, t('account.delete.purchase')), el('li', {}, t('account.delete.books'))),
          () => {
            const now = step();
            if (now.kind === 'password') return SheetPassword(shell, 'delete-pw', 'current-password', setPassword, t('account.delete.password'), 'delete-error');
            return [
              el('p', { class: 'ac-muted' }, t('account.proof.factorLead')),
              passkey(now.need),
              component(TextField, {
                id: 'delete-code',
                label: now.need.totp ? t('account.second.codeLabel') : t('account.second.backupLabel'),
                autocomplete: 'one-time-code',
                'aria-describedby': 'delete-error',
                get value() {
                  return code();
                },
                onChange: (value: string) => {
                  setCode(value);
                },
              }),
            ];
          },
          ErrorText('delete-error', error),
          el(
            'div',
            { class: 'ac-actions ac-confirm__actions' },
            component(Button, {
              class: 'delete-cancel',
              ref: (button: HTMLButtonElement) => {
                queueMicrotask(() => {
                  button.focus();
                });
              },
              onClick: close,
              children: t('account.delete.cancel'),
            }),
            Submit(shell, t('account.delete.submit'), busy, { class: 'delete-submit', variant: 'danger', full: false }),
          ),
        ),
      }),
    ),
  ];
}

/**
 * SP17: the account's sections: with an account, Account, Second sign-in step, Devices, Waiting changes, Recent
 * activity and Your data (the second to the fifth while logged in with a session); without one, the way in; accounts
 * off, what comes later.
 */
export function AccountSections(shell: Shell, heading: (node: HTMLElement) => void): Child {
  const { t, account, ui, manager } = shell;
  // The sections after the first have no entry of their own in the section list: their headings are kept by none.
  const ignore = (): void => undefined;
  // The sections are made again only when what they show of the account changes (another account, signed out, a new
  // address), never on a new copy or time, so a form being filled (the delete form, a list's state) keeps its place.
  const shown = createMemo(
    () => {
      const status = account.status();
      return status.kind === 'account' ? { kind: status.kind, id: status.id, email: status.email, signedOut: status.signedOut } : { kind: status.kind };
    },
    undefined,
    { equals: (a, b) => JSON.stringify(a) === JSON.stringify(b) },
  );
  return () => {
    const status = shown();
    switch (status.kind) {
      case 'off':
      case 'loading':
        return Section('settings-account', t('settings.account'), heading, Later(shell, t('settings.account'), t('settings.accountHelp')));
      case 'none':
        return Section(
          'settings-account',
          t('settings.account'),
          heading,
          Row(
            'setting-account-start',
            t('settings.accountNone'),
            t('settings.accountNoneHelp'),
            component(Button, { variant: 'primary', id: 'setting-account-start', 'aria-describedby': 'setting-account-start-help', onClick: () => ui.setAccountStep('welcome'), children: t('settings.accountStart') }),
            'group',
          ),
        );
      case 'account':
        // The page shown reads the lists (at most once a pause).
        void untrack(() => manager.load());
        return untrack(() => [
          Section('settings-account', t('settings.account'), heading, AccountRows(shell, status.email, status.signedOut)),
          status.signedOut
            ? null
            : [FactorSection(shell, ignore), DevicesSection(shell, ignore), WaitingSection(shell, ignore), ActivitySection(shell, ignore), Section('settings-data', t('account.page.data'), ignore, ExportRow(shell), DeleteRow(shell))],
        ]);
    }
  };
}
